import React from "react";
import {
  Box,
  Dialog,
  Typography,
  Button,
  IconButton,
  Avatar,
  TextField,
  InputAdornment,
  CircularProgress,
  Divider,
  Stack,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import InboxIcon from "@mui/icons-material/Inbox";
import SendIcon from "@mui/icons-material/Send";
import DraftsIcon from "@mui/icons-material/Drafts";
import DeleteIcon from "@mui/icons-material/Delete";
import ReplyIcon from "@mui/icons-material/Reply";
import ForwardIcon from "@mui/icons-material/Forward";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import GoogleIcon from "@mui/icons-material/Google";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";

import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DownloadIcon from "@mui/icons-material/Download";
import ComputerIcon from "@mui/icons-material/Computer";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";

import InlineCompose from "./InlineCompose";
import toast, { Toaster } from "react-hot-toast";
import { api } from "../../../utils/axios-instance";
import { useDepartment } from "../../../stores/departmentStore";
import useAuth from "../../../stores/authStore";
import { useFileStore } from "../../../store/fileStore";
import { useStorageStore } from "../../../store/storageStore";

// ---------------------------------------------------------------------------
// Converts standard base64 → Blob URL.  Avoids data: URI limits & browser
// blocking.  Returned URL must be revoked when no longer needed.
// ---------------------------------------------------------------------------
function base64ToBlobUrl(base64, mimeType) {
  try {
    const byteCharacters = atob(base64);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++)
        byteNumbers[i] = slice.charCodeAt(i);
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    return URL.createObjectURL(new Blob(byteArrays, { type: mimeType }));
  } catch (e) {
    console.error("base64ToBlobUrl error:", e);
    return null;
  }
}

// ---------------------------------------------------------------------------
// AttachmentLightbox — proper centered MUI Dialog so it floats above the
// entire page (not clipped inside the email pane).
// ---------------------------------------------------------------------------
const AttachmentLightbox = ({
  attachment,
  onClose,
  visibleDepartments,
  departmentsLoading,
  ensureDepartmentsLoaded,
}) => {
  const mime = (attachment.mimeType || "").toLowerCase().split(";")[0].trim();
  const name = attachment.filename || attachment.name || "attachment";
  const blobRef = React.useRef(null);

  // Build blob URL once
  if (!blobRef.current && attachment.base64) {
    blobRef.current = base64ToBlobUrl(attachment.base64, mime);
  }
  React.useEffect(
    () => () => {
      if (blobRef.current) URL.revokeObjectURL(blobRef.current);
    },
    []
  );

  const url = blobRef.current;

  const downloadFile = () => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          bgcolor: "#111",
          borderRadius: 3,
          overflow: "hidden",
          maxWidth: "92vw",
          maxHeight: "92vh",
          width: mime === "application/pdf" ? "min(900px, 90vw)" : "auto",
          display: "flex",
          flexDirection: "column",
        },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: "rgba(0,0,0,0.88)" } } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 1.5,
          bgcolor: "rgba(255,255,255,0.06)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            mr: 2,
          }}
        >
          {name}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          {url && (
            // Same split action the attachment row uses: "Download to device"
            // or "Save to a department". Never downloads straight to disk.
            <AttachmentActionsMenu
              attachment={attachment}
              downloadToDevice={downloadFile}
              visibleDepartments={visibleDepartments}
              departmentsLoading={departmentsLoading}
              ensureDepartmentsLoaded={ensureDepartmentsLoaded}
              dark
            />
          )}
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: "rgba(255,255,255,0.7)",
              "&:hover": { color: "#fff" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        {!url ? (
          <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 14 }}>
            Attachment data unavailable
          </Typography>
        ) : mime.startsWith("image/") ? (
          <Box
            component="img"
            src={url}
            alt={name}
            sx={{
              maxWidth: "100%",
              maxHeight: "80vh",
              borderRadius: 2,
              objectFit: "contain",
              boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
            }}
          />
        ) : mime === "application/pdf" ? (
          <Box
            component="iframe"
            src={url}
            title={name}
            sx={{
              width: "100%",
              height: "80vh",
              border: "none",
              borderRadius: 1,
              bgcolor: "#fff",
              display: "block",
            }}
          />
        ) : mime.startsWith("video/") ? (
          <Box
            component="video"
            controls
            autoPlay
            sx={{
              maxWidth: "100%",
              maxHeight: "80vh",
              borderRadius: 2,
              outline: "none",
            }}
          >
            <source src={url} type={mime} />
          </Box>
        ) : mime.startsWith("audio/") ? (
          <Box component="audio" controls autoPlay sx={{ width: 360 }}>
            <source src={url} type={mime} />
          </Box>
        ) : (
          <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 14 }}>
            Preview not available. Use the Download button above.
          </Typography>
        )}
      </Box>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Small helpers for the attachment row
// ---------------------------------------------------------------------------
const PREVIEWABLE_PREFIXES = ["image/", "video/", "audio/"];

const isPreviewableMime = (mime) =>
  mime === "application/pdf" ||
  PREVIEWABLE_PREFIXES.some((p) => mime.startsWith(p));

const getFileTypeIcon = (mime) => {
  if (mime.startsWith("image/"))
    return <ImageIcon sx={{ color: "#9757D7", fontSize: 20 }} />;
  if (mime === "application/pdf")
    return <PictureAsPdfIcon sx={{ color: "#D93025", fontSize: 20 }} />;
  if (mime.startsWith("video/"))
    return <VideoFileIcon sx={{ color: "#374151", fontSize: 20 }} />;
  if (mime.startsWith("audio/"))
    return <AudioFileIcon sx={{ color: "#374151", fontSize: 20 }} />;
  return <InsertDriveFileIcon sx={{ color: "#374151", fontSize: 20 }} />;
};

const formatFileSize = (bytes) => {
  const n = Number(bytes);
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// ---------------------------------------------------------------------------
// AttachmentActionsMenu — the "Download" split action. First level offers
// "Download to device" and "Save to a department"; the latter opens a
// second-level menu populated from the org's department list (the same
// departmentStore SideBar.jsx uses, filtered to what this user can see).
// ---------------------------------------------------------------------------
const AttachmentActionsMenu = ({
  attachment,
  downloadToDevice,
  visibleDepartments,
  departmentsLoading,
  ensureDepartmentsLoaded,
  // `dark` renders the trigger for a dark surface (the lightbox header) and
  // lifts the menus above the lightbox Dialog.
  dark = false,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [deptAnchorEl, setDeptAnchorEl] = React.useState(null);
  const [savingDeptId, setSavingDeptId] = React.useState(null);

  const closeAll = () => {
    setAnchorEl(null);
    setDeptAnchorEl(null);
  };

  const handleSaveToDepartment = async (dept) => {
    const deptId = dept.id || dept.dept_id || dept._id;
    const deptName = dept.name || dept.dept_name || "department";

    const filename = attachment.filename || attachment.name || "attachment";
    const mimeType = attachment.mimeType || "application/octet-stream";
    const content = attachment.base64;
    if (!content) {
      toast.error("Attachment data unavailable");
      return;
    }

    // Gmail already reports the real byte size on the attachment metadata;
    // fall back to estimating from the base64 payload if it's missing.
    const sizeBytes = attachment.size || Math.floor((content.length * 3) / 4);

    const { allowed, message } = useStorageStore
      .getState()
      .checkQuota(sizeBytes);
    if (!allowed) {
      toast.error(message || "Not enough storage for this file.");
      return;
    }

    setSavingDeptId(deptId);
    try {
      await api.post("/users/save-attachment/", {
        department_id: deptId,
        filename,
        mimeType,
        content,
      });
      toast.success(`Saved to ${deptName}`);

      // Same pattern the rest of the file manager uses after an upload:
      // bump the shared refresh key so any mounted Dashboard/Department
      // page refetches, and nudge the storage bar instantly instead of
      // waiting on its next poll.
      useFileStore.getState().triggerFileRefresh();
      useStorageStore.getState().adjustLocal(sizeBytes);
    } catch (err) {
      console.error("Save to department failed:", err);
      const apiMessage = err?.response?.data?.error;
      toast.error(apiMessage || "Failed to save to department");
    } finally {
      setSavingDeptId(null);
      closeAll();
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={
          dark
            ? {
                textTransform: "none",
                fontSize: 12,
                color: "#fff",
                borderColor: "rgba(255,255,255,0.3)",
                "&:hover": {
                  borderColor: "#fff",
                  bgcolor: "rgba(255,255,255,0.08)",
                },
              }
            : {
                textTransform: "none",
                fontSize: 12,
                borderColor: "#E5E7EB",
                color: "#374151",
              }
        }
      >
        Download
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeAll}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={dark ? { zIndex: (t) => t.zIndex.modal + 2 } : undefined}
      >
        <MenuItem
          onClick={() => {
            downloadToDevice();
            closeAll();
          }}
        >
          <ListItemIcon>
            <ComputerIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download to device</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            ensureDepartmentsLoaded();
            setDeptAnchorEl(e.currentTarget);
          }}
        >
          <ListItemIcon>
            <BusinessIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Save to a department</ListItemText>
          <ArrowRightIcon fontSize="small" sx={{ color: "#9A9FA5" }} />
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={deptAnchorEl}
        open={Boolean(deptAnchorEl)}
        onClose={() => setDeptAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={dark ? { zIndex: (t) => t.zIndex.modal + 3 } : undefined}
      >
        {departmentsLoading && (
          <MenuItem disabled sx={{ gap: 1 }}>
            <CircularProgress size={14} /> Loading departments…
          </MenuItem>
        )}
        {!departmentsLoading && visibleDepartments.length === 0 && (
          <MenuItem disabled>No departments found</MenuItem>
        )}
        {!departmentsLoading &&
          visibleDepartments.map((dept) => {
            const deptId = dept.id || dept.dept_id || dept._id;
            const deptName = dept.name || dept.dept_name || deptId;
            return (
              <MenuItem
                key={deptId}
                disabled={savingDeptId === deptId}
                onClick={() => handleSaveToDepartment(dept)}
                sx={{ gap: 1 }}
              >
                {savingDeptId === deptId && <CircularProgress size={14} />}
                {deptName}
              </MenuItem>
            );
          })}
      </Menu>
    </>
  );
};

// ---------------------------------------------------------------------------
// AttachmentRow — a single line inside the shared attachment card. No image
// thumbnails, no raw mime-type strings — just the filename plus Open (for
// previewable types) and Download actions.
// ---------------------------------------------------------------------------
const AttachmentRow = ({
  attachment,
  isLast,
  onPreview,
  visibleDepartments,
  departmentsLoading,
  ensureDepartmentsLoaded,
}) => {
  const mime = (attachment.mimeType || "").toLowerCase().split(";")[0].trim();
  const name = attachment.filename || attachment.name || "attachment";
  const b64 = attachment.base64 || "";
  const blobRef = React.useRef(null);

  React.useEffect(
    () => () => {
      if (blobRef.current) URL.revokeObjectURL(blobRef.current);
    },
    []
  );

  const getBlobUrl = () => {
    if (!b64) return null;
    if (!blobRef.current) blobRef.current = base64ToBlobUrl(b64, mime);
    return blobRef.current;
  };

  const downloadToDevice = () => {
    const url = getBlobUrl();
    if (!url) {
      toast.error("Attachment data not available");
      return;
    }
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderBottom: isLast ? "none" : "1px solid #F3F4F6",
      }}
    >
      <Box sx={{ display: "flex", flexShrink: 0 }}>{getFileTypeIcon(mime)}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          title={name}
          sx={{ color: "#1A1D1F" }}
        >
          {name}
        </Typography>
        {attachment.size ? (
          <Typography variant="caption" color="text.secondary">
            {formatFileSize(attachment.size)}
          </Typography>
        ) : null}
      </Box>
      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
        {isPreviewableMime(mime) && (
          <Button
            size="small"
            variant="text"
            onClick={() => onPreview(attachment)}
            sx={{ textTransform: "none", fontSize: 12, color: "#5E5ADB" }}
          >
            Open
          </Button>
        )}
        <AttachmentActionsMenu
          attachment={attachment}
          downloadToDevice={downloadToDevice}
          visibleDepartments={visibleDepartments}
          departmentsLoading={departmentsLoading}
          ensureDepartmentsLoaded={ensureDepartmentsLoaded}
        />
      </Stack>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// FolderSidebar — pure. Only re-renders when the active folder changes, so
// clicking an email never touches this column.
// ---------------------------------------------------------------------------
const SidebarItem = ({ icon, label, active, count, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      p: 1,
      borderRadius: "10px",
      cursor: "pointer",
      bgcolor: active ? "#F4F4F4" : "transparent",
      "&:hover": { bgcolor: "#F4F4F4" },
    }}
  >
    <Box
      sx={{
        color: active ? "#1A1D1F" : "#6F767E",
        display: "flex",
        fontSize: "16px",
      }}
    >
      {icon}
    </Box>
    <Typography
      variant="caption"
      fontWeight={active ? 700 : 600}
      color={active ? "#1A1D1F" : "#6F767E"}
      sx={{ flex: 1, fontSize: "12px" }}
    >
      {label}
    </Typography>
    {count && (
      <Box
        sx={{
          bgcolor: "#6C5DD3",
          color: "#fff",
          px: 1,
          borderRadius: "8px",
          fontSize: "10px",
          fontWeight: 700,
        }}
      >
        {count}
      </Box>
    )}
  </Box>
);

const FolderSidebar = React.memo(function FolderSidebar({
  activeFolder,
  onCompose,
  onSelectFolder,
}) {
  return (
    <Box
      sx={{
        width: "180px",
        borderRight: "1px solid #EFEFEF",
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        minHeight: 0,
        height: "100%",
        overflowY: "auto",
      }}
    >
      <Button
        onClick={onCompose}
        variant="contained"
        fullWidth
        sx={{
          bgcolor: "#5E5ADB",
          color: "#fff",
          mb: 2,
          borderRadius: "8px",
          textTransform: "none",
          fontWeight: 600,
          "&:hover": { bgcolor: "#4A3AFF" },
          display: "flex",
          gap: 1,
        }}
      >
        <EditIcon fontSize="small" /> Compose
      </Button>

      <SidebarItem
        icon={<InboxIcon />}
        label="Inbox"
        active={activeFolder === "inbox"}
        onClick={() => onSelectFolder("inbox")}
      />
      <SidebarItem
        icon={<SendIcon />}
        label="Sent"
        active={activeFolder === "sent"}
        onClick={() => onSelectFolder("sent")}
      />
      <SidebarItem
        icon={<DraftsIcon />}
        label="Drafts"
        active={activeFolder === "drafts"}
        onClick={() => onSelectFolder("drafts")}
      />
      <SidebarItem
        icon={<DeleteIcon />}
        label="Trash"
        active={activeFolder === "trash"}
        onClick={() => onSelectFolder("trash")}
      />
    </Box>
  );
});

// ---------------------------------------------------------------------------
// EmailListItem — memoized so that when the selection changes, only the two
// affected rows (previously-active + newly-active) re-render. The rest of the
// list is untouched.
// ---------------------------------------------------------------------------
const EmailListItem = React.memo(function EmailListItem({
  email,
  active,
  onClick,
  activeFolder,
}) {
  const extractName = (str) => {
    if (!str) return "Unknown";
    if (typeof str === "object") return str.name || str.address || "Unknown";
    const match = str.match(/^(.*?)(?:\s*<.*>)?$/);
    return match ? match[1].replace(/"/g, "").trim() : str;
  };
  const isSentFolder = activeFolder === "sent" || activeFolder === "drafts";
  const targetRaw = isSentFolder ? email.to : email.from;
  const displayName = isSentFolder
    ? `To: ${extractName(targetRaw)}`
    : extractName(targetRaw);
  const initials =
    extractName(targetRaw)
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase() || "E";

  const formatTimeDifference = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.max(0, Math.floor(diffMs / 60000));
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays > 0) return `${diffDays}d`;
    if (diffHours > 0) return `${diffHours}h`;
    return `${diffMins}m`;
  };
  const timeDisplay =
    email.timeAgo ||
    formatTimeDifference(email.date || email.receivedDateTime) ||
    "Just now";

  const rawPreview =
    email.snippet ||
    email.bodyPreview ||
    (email.body
      ? email.body.replace(/<[^>]*>?/gm, "").trim()
      : "No preview available");
  const previewText =
    rawPreview.length > 50 ? rawPreview.substring(0, 50) + "..." : rawPreview;

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.5,
        cursor: "pointer",
        bgcolor: active ? "#F4F4F4" : "transparent",
        borderLeft: active ? "3px solid #6C5DD3" : "3px solid transparent",
        "&:hover": { bgcolor: "#F4F4F4" },
        borderBottom: "1px solid #EFEFEF",
      }}
    >
      <Box sx={{ display: "flex", gap: 1 }}>
        <Avatar
          sx={{ width: 24, height: 24, bgcolor: "#9757D7", fontSize: "10px" }}
        >
          {initials}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography
              variant="caption"
              fontWeight={700}
              noWrap
              sx={{ fontSize: "12px" }}
            >
              {displayName}
            </Typography>
            <Typography
              variant="caption"
              color="#9A9FA5"
              sx={{ fontSize: "10px" }}
            >
              {timeDisplay}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            fontWeight={700}
            noWrap
            display="block"
            sx={{ mt: 0.2, fontSize: "11px" }}
          >
            {email.subject}
          </Typography>
          <Typography
            variant="caption"
            color="#6F767E"
            noWrap
            display="block"
            sx={{ fontSize: "10px" }}
          >
            {previewText}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

// ---------------------------------------------------------------------------
// EmailListPane — owns the search box + list. Search is local so typing never
// re-renders the reading pane. Memoized: it only re-renders when the list,
// the loading flag, the active folder, or the selected id changes.
// ---------------------------------------------------------------------------
const EmailListPane = React.memo(function EmailListPane({
  emails,
  loading,
  activeFolder,
  selectedId,
  onSelect,
}) {
  const [searchQuery, setSearchQuery] = React.useState("");

  // Reset the search box whenever the folder changes.
  React.useEffect(() => {
    setSearchQuery("");
  }, [activeFolder]);

  const visibleEmails = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return emails;
    return emails.filter((email) => {
      const subjectMatch = email.subject?.toLowerCase().includes(query);
      const fromString =
        typeof email.from === "string"
          ? email.from
          : `${email.from?.name || ""} ${email.from?.address || ""}`;
      const toString =
        typeof email.to === "string"
          ? email.to
          : `${email.to?.name || ""} ${email.to?.address || ""}`;
      return (
        subjectMatch ||
        fromString.toLowerCase().includes(query) ||
        toString.toLowerCase().includes(query)
      );
    });
  }, [emails, searchQuery]);

  return (
    <Box
      sx={{
        width: "260px",
        borderRight: "1px solid #EFEFEF",
        display: "grid",
        gridTemplateRows: "auto 1fr",
        minHeight: 0,
        height: "100%",
      }}
    >
      <Box sx={{ p: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search mail..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9A9FA5", fontSize: "20px" }} />
              </InputAdornment>
            ),
            sx: {
              borderRadius: "12px",
              bgcolor: "#F4F4F4",
              "& fieldset": { border: "none" },
            },
          }}
        />
      </Box>

      <Box sx={{ overflowY: "auto", minHeight: 0, position: "relative" }}>
        {/* Folder-switch spinner: shown only when the full list reloads.
            Never triggers when clicking an individual email. */}
        {loading && emails.length === 0 && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!loading && visibleEmails.length === 0 && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {searchQuery ? "No matching emails." : "No emails found."}
            </Typography>
          </Box>
        )}
        {visibleEmails.map((email) => (
          <EmailListItem
            key={email.id}
            email={email}
            activeFolder={activeFolder}
            active={selectedId === email.id}
            onClick={() => onSelect(email)}
          />
        ))}
      </Box>
    </Box>
  );
});

// ---------------------------------------------------------------------------
// ReadingPane — the rightmost column. This is the ONLY pane that re-renders
// when a different email is opened. Zoom + lightbox are local UI state so they
// stay contained here too.
// ---------------------------------------------------------------------------
const ReadingPane = React.memo(function ReadingPane({
  selectedEmail,
  bodyLoading,
  activeFolder,
  gmailEmail,
  visibleDepartments,
  departmentsLoading,
  ensureDepartmentsLoaded,
}) {
  const [zoom, setZoom] = React.useState(1);
  const [lightboxAttachment, setLightboxAttachment] = React.useState(null);

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.1, 2));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 0.5));

  // Reset zoom each time a new email is opened.
  React.useEffect(() => {
    setZoom(1);
  }, [selectedEmail?.id]);

  const isSentFolder = activeFolder === "sent" || activeFolder === "drafts";

  return (
    <Box
      sx={{
        flex: 1,
        display: "grid",
        gridTemplateRows: selectedEmail ? "auto auto 1fr" : "1fr",
        minWidth: 0,
        height: "100%",
        bgcolor: "#fff",
        position: "relative",
      }}
    >
      {/* Body loading overlay — covers only this pane, list stays intact */}
      {bodyLoading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.75)",
          }}
        >
          <CircularProgress size={28} sx={{ color: "#5E5ADB" }} />
        </Box>
      )}

      {/* Attachment lightbox — renders as a top-level Dialog */}
      {lightboxAttachment && (
        <AttachmentLightbox
          attachment={lightboxAttachment}
          onClose={() => setLightboxAttachment(null)}
          visibleDepartments={visibleDepartments}
          departmentsLoading={departmentsLoading}
          ensureDepartmentsLoaded={ensureDepartmentsLoaded}
        />
      )}

      {selectedEmail ? (
        <>
          {/* Subject */}
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              borderBottom: "1px solid #F4F4F4",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              color="#1A1D1F"
              sx={{ flex: 1, mr: 2 }}
            >
              {selectedEmail.subject || "(No subject)"}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  bgcolor: "#F4F4F4",
                  borderRadius: "8px",
                  p: 0.5,
                  mr: 1,
                }}
              >
                <IconButton
                  size="small"
                  onClick={handleZoomOut}
                  disabled={zoom <= 0.5}
                  sx={{ p: 0.5 }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: "16px" }}>
                    -
                  </Typography>
                </IconButton>
                <Typography
                  sx={{
                    mx: 1,
                    fontSize: "12px",
                    fontWeight: 700,
                    width: "35px",
                    textAlign: "center",
                  }}
                >
                  {Math.round(zoom * 100)}%
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleZoomIn}
                  disabled={zoom >= 2}
                  sx={{ p: 0.5 }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: "16px" }}>
                    +
                  </Typography>
                </IconButton>
              </Box>
            </Stack>
          </Box>

          {/* Sender Info */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              borderBottom: "1px solid #F4F4F4",
            }}
          >
            {(() => {
              const parsePerson = (str) => {
                if (!str) return { name: "Unknown", email: "" };
                if (typeof str === "object")
                  return {
                    name: str.name || "Unknown",
                    email: str.address ? `<${str.address}>` : "",
                  };
                const match = str.match(/^(.*?)(?:\s*(<.*>))?$/);
                return match
                  ? {
                      name: match[1].replace(/"/g, "").trim(),
                      email: match[2] || "",
                    }
                  : { name: str, email: "" };
              };
              const personRaw = isSentFolder
                ? selectedEmail.to
                : selectedEmail.from;
              const person = parsePerson(personRaw);
              const initials =
                person.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .substring(0, 2)
                  .toUpperCase() || "E";
              return (
                <>
                  <Avatar
                    sx={{
                      bgcolor: "#9757D7",
                      width: 32,
                      height: 32,
                      fontSize: "12px",
                    }}
                  >
                    {initials}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="#1A1D1F"
                    >
                      {isSentFolder ? `To: ${person.name}` : person.name}{" "}
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 500,
                          color: "#6F767E",
                          ml: 1,
                        }}
                      >
                        {person.email}
                      </Box>
                    </Typography>
                    <Typography variant="caption" color="#9A9FA5">
                      {isSentFolder ? `From: ${gmailEmail || "Me"}` : "To: Me"}
                    </Typography>
                  </Box>
                </>
              );
            })()}
          </Box>

          {/* Scrollable Email Body */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ flex: 1, p: 2, minHeight: 0 }}>
              <iframe
                title="email-content"
                srcDoc={`
                <!DOCTYPE html>
                <html>
                  <head>
                    <style>
                      body {
                        margin: 0; padding: 0;
                        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                        color: #1A1D1F; font-size: 13px; line-height: 1.6;
                        zoom: ${zoom};
                        overflow-wrap: break-word; word-wrap: break-word;
                      }
                      img { max-width: 100%; height: auto; }
                    </style>
                  </head>
                  <body>${selectedEmail.body || ""}</body>
                </html>`}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  display: "block",
                }}
              />
            </Box>

            {selectedEmail?.attachments?.length > 0 && (
              <Box sx={{ borderTop: "1px solid #EFEFEF", p: 2 }}>
                <Box
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "10px",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      bgcolor: "#FAFAFA",
                      borderBottom: "1px solid #E5E7EB",
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="#6F767E"
                    >
                      {selectedEmail.attachments.length} Attachment
                      {selectedEmail.attachments.length > 1 ? "s" : ""}
                    </Typography>
                  </Box>
                  {selectedEmail.attachments.map((file, index) => (
                    <AttachmentRow
                      key={index}
                      attachment={file}
                      isLast={index === selectedEmail.attachments.length - 1}
                      onPreview={(att) => setLightboxAttachment(att)}
                      visibleDepartments={visibleDepartments}
                      departmentsLoading={departmentsLoading}
                      ensureDepartmentsLoaded={ensureDepartmentsLoaded}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography color="#9A9FA5">
            {bodyLoading ? "Loading…" : "No email to display"}
          </Typography>
        </Box>
      )}
    </Box>
  );
});

// ---------------------------------------------------------------------------
// MailFullClient — owns selection state for the reading pane. Because the
// three columns above are memoized, opening an email only re-renders the
// reading pane (plus the two list rows whose highlight changes).
// ---------------------------------------------------------------------------
const MailFullClient = ({
  open,
  onClose,
  emails,
  onFetchEmailBody,
  activeFolder,
  setActiveFolder,
  activeProvider,
  setActiveProvider,
  gmailConnected,
  gmailEmail,
  gmailProfilePic,
  onLogout,
  onRefresh,
  loading,
  darkMode,
}) => {
  const [isComposing, setIsComposing] = React.useState(false);
  const [selectedEmail, setSelectedEmail] = React.useState(null);
  const [bodyLoading, setBodyLoading] = React.useState(false);
  const [draftContent, setDraftContent] = React.useState({
    to: "",
    subject: "",
    content: "",
  });

  // Tracks the id of the email whose body we most recently *requested*. Used to
  // (a) guard against out-of-order responses and (b) decide whether the
  // current selection is still valid after the list changes.
  const requestedIdRef = React.useRef(null);

  // ── Departments for "Save attachment to a department" ───────────────────
  const { profileData } = useAuth();
  const {
    departments,
    listDepartments,
    loading: departmentsLoading,
  } = useDepartment();

  const visibleDepartments = React.useMemo(() => {
    if (!profileData) return [];
    if (
      profileData.role_priv === "org_admin" ||
      profileData.role_priv === "super_admin"
    ) {
      return departments || [];
    }
    const ids = profileData.dept_ids || [];
    const names = profileData.dept_names || [];
    if (ids.length)
      return (departments || []).filter((d) => ids.includes(d.id));
    if (names.length)
      return (departments || []).filter((d) => names.includes(d.name));
    return [];
  }, [profileData, departments]);

  const ensureDepartmentsLoaded = React.useCallback(() => {
    if (!departments || departments.length === 0) {
      listDepartments();
    }
  }, [departments, listDepartments]);

  // ── Open a single email into the reading pane ───────────────────────────
  // Fetches the body, guards against races, and merges the full payload over
  // the list-row metadata we already have (so subject/from render instantly).
  const openEmail = React.useCallback(
    async (email) => {
      if (!email) return;

      // A locally-saved draft opens the composer instead of the reader.
      if (email.isLocalDraft) {
        setIsComposing(true);
        return;
      }

      requestedIdRef.current = email.id;
      // Seed the pane with what the list already knows so the header paints
      // immediately; the body fills in when the fetch resolves.
      setSelectedEmail((prev) =>
        prev?.id === email.id ? prev : { ...email, body: prev?.body ?? "" }
      );
      setBodyLoading(true);

      try {
        const full = await onFetchEmailBody(email.id);
        if (requestedIdRef.current !== email.id) return; // stale response
        setSelectedEmail({ ...email, ...full });
      } catch (err) {
        if (requestedIdRef.current !== email.id) return;
        console.error(`Failed to load email ${email.id}:`, err);
        toast.error("Failed to load email");
      } finally {
        if (requestedIdRef.current === email.id) setBodyLoading(false);
      }
    },
    [onFetchEmailBody]
  );

  // Build the list the pane actually shows, injecting a local draft when the
  // Drafts folder is active. Memoized so its identity is stable across
  // selection changes (keeps EmailListPane from re-rendering needlessly).
  const displayEmails = React.useMemo(() => {
    const hasDraft =
      draftContent.to || draftContent.subject || draftContent.content;
    if (activeFolder === "drafts" && hasDraft) {
      return [
        {
          id: "local-draft",
          subject: draftContent.subject || "(No subject)",
          from: {
            name: "Draft",
            address: draftContent.to || "(No recipient)",
          },
          timeAgo: "Just now",
          isLocalDraft: true,
          body: draftContent.content || "(Empty)",
        },
        ...emails,
      ];
    }
    return emails;
  }, [emails, activeFolder, draftContent]);

  // ── Auto-open the first/latest email when a folder's list loads ─────────
  // Runs on open, folder switch, and refetch. If the previously-open email is
  // still present we keep it; otherwise we open the top of the list. Skipped
  // while composing or loading so we never fight the user.
  React.useEffect(() => {
    if (!open || isComposing || loading) return;

    if (displayEmails.length === 0) {
      requestedIdRef.current = null;
      setSelectedEmail(null);
      return;
    }

    const firstOpenable = displayEmails.find((e) => !e.isLocalDraft);
    const currentStillPresent =
      requestedIdRef.current &&
      displayEmails.some((e) => e.id === requestedIdRef.current);

    if (!currentStillPresent && firstOpenable) {
      openEmail(firstOpenable);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayEmails, open, isComposing, loading]);

  // Reset everything when the dialog closes so the next open starts clean.
  React.useEffect(() => {
    if (!open) {
      requestedIdRef.current = null;
      setSelectedEmail(null);
      setBodyLoading(false);
      setIsComposing(false);
    }
  }, [open]);

  // ── Stable callbacks so the memoized panes don't re-render pointlessly ──
  const handleSelectFolder = React.useCallback(
    (folder) => {
      setActiveFolder(folder);
      if (isComposing) {
        if (draftContent.to || draftContent.subject || draftContent.content) {
          toast.success("Draft saved locally");
        }
        setIsComposing(false);
      }
    },
    [setActiveFolder, isComposing, draftContent]
  );

  const handleCompose = React.useCallback(() => setIsComposing(true), []);

  const selectedId = selectedEmail?.id ?? requestedIdRef.current ?? null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          height: "85vh",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: "1px solid #EFEFEF",
          boxShadow: "0px 40px 64px -12px rgba(0, 0, 0, 0.08)",
        },
      }}
    >
      <Toaster position="bottom-left" reverseOrder={false} />
      {/* Header / Provider Toggle */}
      <Box
        sx={{
          height: "52px",
          bgcolor: "#FCFCFC",
          borderBottom: "1px solid #EFEFEF",
          display: "flex",
          alignItems: "center",
          px: 2,
          justifyContent: "space-between",
        }}
      >
        <Typography variant="body2" fontWeight={700} color="#1A1D1F">
          Mail — {gmailEmail}
        </Typography>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {gmailConnected && (
            <Tooltip title="Refresh mail">
              {/* span wrapper keeps the tooltip working while the button is
                  disabled mid-refresh */}
              <span>
                <IconButton
                  size="small"
                  onClick={onRefresh}
                  disabled={loading || !onRefresh}
                  sx={{
                    color: "#6F767E",
                    "&:hover": { color: "#1A1D1F", bgcolor: "#F4F4F4" },
                    // Spin the icon while the folder is reloading.
                    "@keyframes mailRefreshSpin": {
                      from: { transform: "rotate(0deg)" },
                      to: { transform: "rotate(360deg)" },
                    },
                    ...(loading && {
                      animation: "mailRefreshSpin 0.9s linear infinite",
                    }),
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {gmailConnected && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Chip
                icon={
                  gmailProfilePic ? (
                    <Avatar
                      src={gmailProfilePic}
                      sx={{ width: 16, height: 16, ml: 1 }}
                      imgProps={{ referrerPolicy: "no-referrer" }}
                    />
                  ) : (
                    <GoogleIcon sx={{ fontSize: "16px !important" }} />
                  )
                }
                label="Gmail"
                onClick={() => setActiveProvider("gmail")}
                sx={{
                  height: "28px",
                  bgcolor:
                    activeProvider === "gmail" ? "#1A1D1F" : "transparent",
                  color: activeProvider === "gmail" ? "#fff" : "#1A1D1F",
                  fontWeight: 600,
                  "& .MuiChip-icon": {
                    color: activeProvider === "gmail" ? "#fff" : "#EA4335",
                  },
                }}
              />
              <button
                onClick={() => onLogout("gmail")}
                className="text-xs text-red-500 hover:text-red-700 font-bold ml-1"
              >
                Logout
              </button>
            </Stack>
          )}
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Column 1 — folders (memoized: never re-renders on email click) */}
        <FolderSidebar
          activeFolder={activeFolder}
          onCompose={handleCompose}
          onSelectFolder={handleSelectFolder}
        />

        {isComposing ? (
          <InlineCompose
            onClose={() => setIsComposing(false)}
            onDiscard={() => {
              setDraftContent({ to: "", subject: "", content: "" });
              setIsComposing(false);
            }}
            mailContent={draftContent}
            setMailContent={setDraftContent}
          />
        ) : (
          <>
            {/* Column 2 — email list (memoized: only the highlight moves) */}
            <EmailListPane
              emails={displayEmails}
              loading={loading}
              activeFolder={activeFolder}
              selectedId={selectedId}
              onSelect={openEmail}
            />

            {/* Column 3 — reading pane (the ONLY pane that refreshes) */}
            <ReadingPane
              selectedEmail={selectedEmail}
              bodyLoading={bodyLoading}
              activeFolder={activeFolder}
              gmailEmail={gmailEmail}
              visibleDepartments={visibleDepartments}
              departmentsLoading={departmentsLoading}
              ensureDepartmentsLoaded={ensureDepartmentsLoaded}
            />
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default MailFullClient;
